import { addDays, isoDay } from 'helper/timeLabels';

/**
 * The demo database of the CRM pages (home, tasks, the task panel and form),
 * one per state of `?mock=`. `crmApi` reads its views from it and writes into
 * it, as the api will with the real database. Dates are ISO strings, days
 * `YYYY-MM-DD` in local time, times `HH:mm`. `me` is the signed-in user: the
 * tasks of the demo are theirs unless given to a colleague.
 */

export const CRM_SCENARIOS = [
  'journee',
  'chargement',
  'erreur',
  'vide',
  'sans-rdv',
  'a-qualifier',
  'appels-termines',
  'responsable',
  'a-jour',
];
export const DEFAULT_CRM_SCENARIO = 'journee';

// `role` as admin/crm/team sends it (JBWEB-1405): the recruiter menus offer
// only the 520 and the 500.
const COLLEAGUES = [
  { id: 'u2', firstname: 'Cheyenne', lastname: 'Louis', role: 520 },
  { id: 'u3', firstname: 'Fabien', lastname: 'Marie', role: 500 },
  { id: 'u4', firstname: 'Ludovic', lastname: 'René', role: 515 },
];

const COMPANIES = [
  { id: 'c1', name: 'Distri Madinina', city: 'Fort-de-France', contactName: 'Alain Rosier', contactRole: 'Gérant', phone: '0596 60 12 34', email: 'alain.rosier@madinina.mq', stage: 'client' },
  { id: 'c2', name: 'Caraïbes Logistique', city: 'Le Lamentin', contactName: 'Paul Ernest', contactRole: 'Directeur', phone: '0596 51 22 10', email: 'p.ernest@caraibes-logistique.mq', stage: 'client' },
  { id: 'c3', name: 'Hôtel Bakoua', city: 'Les Trois-Îlets', contactName: 'Sophie Laurent', contactRole: 'Responsable RH', phone: '0596 66 02 02', email: 'rh@bakoua.mq', stage: 'prospect' },
  { id: 'c4', name: 'Boulangerie Ti Pain', city: 'Le Robert', contactName: 'Marie Joseph', contactRole: 'Gérante', phone: '0596 65 40 40', email: 'contact@tipain.mq', stage: 'prospect' },
  { id: 'c5', name: 'Pharmacie du Port', city: 'Fort-de-France', contactName: 'Julie Marc', contactRole: 'Pharmacienne', phone: '0596 71 10 10', email: 'julie.marc@pharmacieduport.mq', stage: 'prospect' },
  { id: 'c6', name: 'Garage Central', city: 'Schœlcher', contactName: 'Henri Valy', contactRole: 'Chef d’atelier', phone: '0596 61 30 30', email: 'garage.central@orange.mq', stage: 'prospect' },
  { id: 'c7', name: 'Supérette Bô Kay', city: 'Le François', contactName: 'Nadia Pierre', contactRole: 'Gérante', phone: '0596 54 20 20', email: 'bokay@wanadoo.mq', stage: 'client' },
  { id: 'c8', name: 'Restaurant La Case', city: 'Sainte-Anne', contactName: 'Marc Édouard', contactRole: 'Chef', phone: '0596 76 80 80', email: 'lacase@gmail.com', stage: 'prospect' },
  { id: 'c9', name: 'Clinique Sainte-Marie', city: 'Sainte-Marie', contactName: 'Claire Dumas', contactRole: 'DRH', phone: '0596 69 00 00', email: 'drh@clinique-sm.mq', stage: 'client' },
];
const at = (now, daysAgo, hours, minutes = 0) => {
  const date = addDays(now, -daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};
const inMinutes = (now, minutes) => new Date(now.getTime() + minutes * 60000).toISOString();

const task = (now, me, id, type, title, inDays, fields = {}) => ({
  id,
  type,
  title,
  note: '',
  dueOn: isoDay(addDays(now, inDays)),
  dueTime: null,
  durationMin: type === 'meeting' ? 30 : 15,
  doneAt: null,
  contactName: '',
  companyId: null,
  assigneeId: me.id,
  callbackId: null,
  appointmentId: null,
  emailTemplate: '',
  emailSubject: '',
  emailBody: '',
  unread: false,
  createdAt: at(now, 10, 9),
  createdBy: me.id,
  ...fields,
});

const appointment = (now, id, category, from, to, fields) => ({
  id,
  category,
  title: '',
  description: '',
  location: '',
  startsAt: at(now, 0, ...from),
  endsAt: at(now, 0, ...to),
  status: 'planned',
  contactName: '',
  companyId: null,
  ...fields,
});

const nameOf = (person) => [person.firstname, person.lastname].filter(Boolean).join(' ');

// The other appointments of the week, by weekday (1 is Monday): held when
// they are over, planned otherwise. Today's are those above.
const onWeekday = (now, weekday, [hours, minutes]) => {
  const date = addDays(now, weekday - (((now.getDay() + 6) % 7) + 1));
  date.setHours(hours, minutes, 0, 0);
  return date;
};
const weekAppointment = (now, id, weekday, category, from, to, fields) => {
  const startsAt = onWeekday(now, weekday, from);
  const endsAt = onWeekday(now, weekday, to);
  return {
    ...appointment(now, id, category, from, to, fields),
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    status: endsAt < now ? 'done' : 'planned',
  };
};
const weekAppointments = (now) =>
  [
    weekAppointment(now, 'w1', 1, 'prospection', [9, 0], [10, 0], { companyId: 'c6', contactName: 'Henri Valy', location: 'Schœlcher' }),
    weekAppointment(now, 'w2', 1, 'visite_client', [14, 30], [15, 15], { companyId: 'c2', contactName: 'Paul Ernest', location: 'Le Lamentin' }),
    weekAppointment(now, 'w3', 2, 'rdv_client', [10, 0], [10, 45], { companyId: 'c9', contactName: 'Claire Dumas', location: 'Sainte-Marie' }),
    weekAppointment(now, 'w4', 2, 'embauche', [15, 0], [16, 0], { companyId: 'c7', contactName: 'Nadia Pierre', location: 'Le François' }),
    weekAppointment(now, 'w5', 4, 'prospection', [9, 30], [10, 30], { companyId: 'c5', contactName: 'Julie Marc', location: 'Fort-de-France' }),
    weekAppointment(now, 'w6', 4, 'prospection', [11, 0], [11, 45], {
      companyId: 'c3',
      contactName: 'Sophie Laurent',
      location: 'Les Trois-Îlets',
      travelMinutes: 30,
    }),
    weekAppointment(now, 'w7', 5, 'interne', [9, 0], [10, 0], { title: 'Réunion d’agence', description: 'Bilan de la semaine' }),
    weekAppointment(now, 'w8', 5, 'visite_client', [14, 0], [15, 0], { companyId: 'c1', contactName: 'Alain Rosier', location: 'Fort-de-France' }),
  ]
    // Today has its own appointments.
    .filter((item) => isoDay(new Date(item.startsAt)) !== isoDay(now));

// A visit booked while prospecting, still to be placed in the week before `dueBy`.
const visit = (now, id, category, companyId, inDays, reason, durationMin = 45) => ({
  id,
  category,
  companyId,
  dueBy: isoDay(addDays(now, inDays)),
  reason,
  durationMin,
});

const fullDay = (now, me) => ({
  team: [{ ...me, role: 510 }, ...COLLEAGUES],
  companies: COMPANIES,
  appointments: [
    appointment(now, 'a1', 'interne', [8, 30], [9, 0], {
      title: 'Point équipe commerciale',
      description: 'Salle de réunion',
      status: 'done',
    }),
    appointment(now, 'a2', 'prospection', [10, 0], [11, 0], {
      companyId: 'c4',
      contactName: 'Marie Joseph',
      location: 'Le Robert',
    }),
    appointment(now, 'a3', 'visite_client', [14, 0], [14, 45], {
      companyId: 'c1',
      contactName: 'Alain Rosier',
      location: 'Fort-de-France',
    }),
    appointment(now, 'a4', 'telephone', [16, 30], [17, 0], {
      companyId: 'c3',
      contactName: 'Sophie Laurent',
      description: 'Point sur la saison',
    }),
    appointment(now, 'g1', 'interne', [18, 0], [18, 30], {
      title: 'Afterwork de l’agence',
      description: 'Google Agenda',
      externalLink: 'https://calendar.google.com/',
    }),
    ...weekAppointments(now),
  ],
  visits: [
    visit(now, 'v1', 'prospection', 'c8', 1, 'RDV pris en prospection'),
    visit(now, 'v2', 'prospection', 'c4', 3, 'Présenter la grille tarifaire'),
    visit(now, 'v3', 'visite_client', 'c7', 6, 'Point sur les intérimaires', 60),
  ],
  tasks: [
    task(now, me, 't1', 'todo', 'Envoyer la plaquette tarifaire', -1, { companyId: 'c6' }),
    task(now, me, 't2', 'email', 'Relancer la proposition', -2, {
      companyId: 'c5',
      contactName: 'Julie Marc',
      unread: true,
      emailTemplate: 'Relance de proposition',
      emailSubject: 'Notre proposition pour la Pharmacie du Port',
      emailBody: 'Bonjour Julie,\n\nJe reviens vers vous au sujet de notre proposition de la semaine dernière. Avez-vous pu la regarder ?\n\nBelle journée,',
    }),
    task(now, me, 't3', 'call', 'Rappeler pour le devis', -1, {
      dueTime: '16:00',
      companyId: 'c2',
      contactName: 'Paul Ernest',
    }),
    task(now, me, 't4', 'call', 'Confirmer le volume de juin', 0, { dueTime: '11:00', companyId: 'c2' }),
    task(now, me, 't5', 'visit', 'Déposer les contrats signés', -3, { companyId: 'c7' }),
    task(now, me, 't6', 'todo', 'Mettre à jour la fiche client', -6, { companyId: 'c8' }),
    task(now, me, 't7', 'todo', 'Préparer le point mensuel', -9),
    task(now, me, 't8', 'email', 'Envoyer le récapitulatif mensuel', 0, {
      dueTime: '11:30',
      companyId: 'c1',
      contactName: 'Alain Rosier',
    }),
    task(now, me, 't9', 'todo', 'Valider les relevés d’heures', 0, { dueTime: '15:30' }),
    task(now, me, 't10', 'call', 'Proposer un créneau de visite', 0, {
      companyId: 'c3',
      contactName: 'Sophie Laurent',
      unread: true,
    }),
    task(now, me, 't11', 'todo', 'Commander les badges d’accès', 0, {
      note: 'Trois badges pour les nouveaux du chantier de Caraïbes Logistique.',
    }),
    task(now, me, 't12', 'todo', 'Répondre au mail de la comptabilité', 0, { doneAt: at(now, 0, 9, 12) }),
    task(now, me, 't13', 'call', 'Rappeler la clinique', 0, {
      doneAt: at(now, 0, 9, 40),
      companyId: 'c9',
      contactName: 'Claire Dumas',
    }),
    // Repeats a callback of the call list: no row of its own on the home page.
    task(now, me, 't14', 'call', 'Rappeler la boulangerie', 0, { companyId: 'c4', callbackId: 'cb1' }),
    // Repeats the appointment a2: no row of its own on the home page.
    task(now, me, 't15', 'meeting', 'Visite terrain', 0, { companyId: 'c4', appointmentId: 'a2' }),
    task(now, me, 't16', 'meeting', 'Point avec la direction', 1, { dueTime: '09:30', durationMin: 60 }),
    task(now, me, 't17', 'call', 'Relancer le devis signé', 1, { companyId: 'c1', contactName: 'Alain Rosier' }),
    task(now, me, 't18', 'visit', 'Visite de suivi du chantier', 4, { dueTime: '10:00', companyId: 'c2' }),
    task(now, me, 't19', 'todo', 'Préparer la réunion trimestrielle', 8),
    task(now, me, 't20', 'email', 'Envoyer les vœux de fin d’année', 20, { companyId: 'c4' }),
    task(now, me, 't21', 'email', 'Envoyer le devis', -2, { doneAt: at(now, 2, 11, 5), companyId: 'c1' }),
    // The tasks of colleagues, for an agency manager who looks at theirs.
    task(now, me, 't30', 'call', 'Appeler les intérimaires du chantier', 0, { assigneeId: 'u2', companyId: 'c2' }),
    task(now, me, 't31', 'todo', 'Valider les contrats de la semaine', -1, { assigneeId: 'u3' }),
    task(now, me, 't32', 'visit', 'Préparer la tournée de jeudi', 1, { assigneeId: 'u4', companyId: 'c7' }),
  ],
  callbacks: [
    { id: 'cb1', kind: 'callback', dueAt: inMinutes(now, -45), doneAt: null, companyId: 'c4' },
    { id: 'cb2', kind: 'retry', dueAt: inMinutes(now, 90), doneAt: null, companyId: 'c6' },
    { id: 'cb3', kind: 'new', dueAt: at(now, 0, 0), doneAt: null, companyId: 'c8' },
    { id: 'cb4', kind: 'new', dueAt: at(now, 0, 0), doneAt: null, companyId: 'c7' },
    { id: 'cb9', kind: 'callback', dueAt: at(now, 1, 15), doneAt: at(now, 1, 15, 20), companyId: 'c6' },
  ],
  calls: {
    made: [
      { id: 'k1', result: 'answered', companyId: 'c10' },
      { id: 'k2', result: 'no_answer', companyId: 'c11' },
      { id: 'k3', result: 'callback', companyId: 'c12' },
      { id: 'k4', result: 'no_answer', companyId: 'c13' },
      { id: 'k5', result: 'answered', companyId: 'c14' },
      { id: 'k6', result: 'no_answer', companyId: 'c15' },
    ],
    toQualify: 0,
    appointmentsBooked: 2,
  },
  prospects: true,
  comments: {
    t2: [
      { id: 'm1', authorId: 'u2', body: 'Julie m’a dit qu’elle attendait le retour de son associé.', createdAt: at(now, 1, 14, 5) },
    ],
    // The comments that name the user (`mentions`) fill « Mentions » on the home page.
    t4: [
      {
        id: 'm3',
        authorId: 'u2',
        body: `@${nameOf(me)} le client préfère qu’on l’appelle avant midi.`,
        mentions: [me.id],
        createdAt: at(now, 0, 10, 12),
      },
    ],
    t10: [
      {
        id: 'm2',
        authorId: 'u3',
        body: `@${nameOf(me)} pense à leur proposer le jeudi matin.`,
        mentions: [me.id],
        createdAt: at(now, 0, 8, 50),
      },
    ],
    t30: [
      {
        id: 'm4',
        authorId: 'u4',
        body: `@${nameOf(me)} tu peux me passer le numéro du chef de chantier ?`,
        mentions: [me.id],
        createdAt: at(now, 1, 17, 40),
      },
    ],
  },
  activity: {},
});

const BUILDERS = {
  journee: fullDay,
  vide: (now, me) => ({
    ...fullDay(now, me),
    appointments: [],
    tasks: [],
    callbacks: [],
    calls: { made: [], toQualify: 0, appointmentsBooked: 0 },
    visits: [],
    comments: {},
  }),
  'sans-rdv': (now, me) => {
    const day = fullDay(now, me);
    return {
      ...day,
      appointments: [],
      tasks: day.tasks.filter((item) => !item.appointmentId),
      visits: day.visits.slice(1),
    };
  },
  'a-qualifier': (now, me) => {
    const day = fullDay(now, me);
    return { ...day, calls: { ...day.calls, toQualify: 2 } };
  },
  'appels-termines': (now, me) => {
    const day = fullDay(now, me);
    return {
      ...day,
      tasks: day.tasks.filter((item) => !item.callbackId),
      callbacks: day.callbacks.map((item) => (item.doneAt ? item : { ...item, doneAt: at(now, 0, 10, 30) })),
    };
  },
  // An agency manager does not prospect: no call list.
  responsable: (now, me) => ({ ...fullDay(now, me), prospects: false }),
  // Everything done: nothing left to do.
  'a-jour': (now, me) => {
    const day = fullDay(now, me);
    return { ...day, tasks: day.tasks.filter((item) => item.doneAt) };
  },
};

export const buildCrmMock = (scenario, me, now = new Date()) =>
  (BUILDERS[scenario] ?? BUILDERS[DEFAULT_CRM_SCENARIO])(now, me);
